import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Chip,
  IconButton,
  Tooltip,

} from '@mui/material';
import {
  WhatsApp,
  Send,
  ContentCopy,
  Edit,
  CheckCircle,
  Schedule,
  LocationOn,
} from '@mui/icons-material';

interface WhatsAppMessage {
  id: string;
  template: string;
  variables: string[];
  description: string;
  category: 'status' | 'location' | 'task' | 'emergency';
}

interface WhatsAppIntegrationProps {
  onMessageSent?: (message: string) => void;
  currentLocation?: string;
  currentTask?: string;
  userStatus?: string;
}

export default function WhatsAppIntegration({
  onMessageSent,
  currentLocation = 'North Gate',
  currentTask = 'Buggy Operations',
  userStatus = 'On Duty'
}: WhatsAppIntegrationProps) {

  const [open, setOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<WhatsAppMessage | null>(null);
  const [customMessage, setCustomMessage] = useState('');
  const [copied, setCopied] = useState(false);

  // Pre-defined message templates
  const messageTemplates: WhatsAppMessage[] = [
    {
      id: '1',
      template: 'Buggy {number} arrived @ {location}',
      variables: ['number', 'location'],
      description: 'Arrival notification',
      category: 'location'
    },
    {
      id: '2',
      template: 'Task completed: {task} @ {location}',
      variables: ['task', 'location'],
      description: 'Task completion',
      category: 'task'
    },
    {
      id: '3',
      template: 'Status update: {status} - {details}',
      variables: ['status', 'details'],
      description: 'Status update',
      category: 'status'
    },
    {
      id: '4',
      template: 'Issue reported at {location}: {issue}',
      variables: ['location', 'issue'],
      description: 'Issue reporting',
      category: 'emergency'
    },
    {
      id: '5',
      template: 'HOTO event: {event} @ {time}',
      variables: ['event', 'time'],
      description: 'HOTO event logging',
      category: 'task'
    }
  ];

  const generateMessage = (template: WhatsAppMessage): string => {
    let message = template.template;
    
    // Replace variables with actual values
    if (template.variables.includes('location')) {
      message = message.replace('{location}', currentLocation);
    }
    if (template.variables.includes('task')) {
      message = message.replace('{task}', currentTask);
    }
    if (template.variables.includes('status')) {
      message = message.replace('{status}', userStatus);
    }
    if (template.variables.includes('number')) {
      message = message.replace('{number}', '4'); // This could be dynamic based on user
    }
    if (template.variables.includes('time')) {
      message = message.replace('{time}', new Date().toLocaleTimeString());
    }
    if (template.variables.includes('details')) {
      message = message.replace('{details}', 'All systems operational');
    }
    if (template.variables.includes('issue')) {
      message = message.replace('{issue}', 'Minor delay - resolved');
    }
    if (template.variables.includes('event')) {
      message = message.replace('{event}', 'Zone clearance');
    }

    return message;
  };

  const handleTemplateSelect = (template: WhatsAppMessage) => {
    setSelectedMessage(template);
    setCustomMessage(generateMessage(template));
  };

  const handleSendMessage = () => {
    if (!customMessage.trim()) return;

    // Open WhatsApp with the message
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(customMessage)}`;
    window.open(whatsappUrl, '_blank');

    // Notify parent component
    onMessageSent?.(customMessage);

    // Close dialog
    setOpen(false);
    setSelectedMessage(null);
    setCustomMessage('');
  };

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(customMessage);
    setCopied(true);
            setTimeout(() => setCopied(false), 500);
  };

  const getCategoryIcon = (category: WhatsAppMessage['category']) => {
    switch (category) {
      case 'location': return <LocationOn />;
      case 'task': return <CheckCircle />;
      case 'status': return <Schedule />;
      case 'emergency': return <Typography variant="h6">🚨</Typography>;
      default: return <Send />;
    }
  };

  const getCategoryColor = (category: WhatsAppMessage['category']) => {
    switch (category) {
      case 'location': return 'primary';
      case 'task': return 'success';
      case 'status': return 'info';
      case 'emergency': return 'error';
      default: return 'default';
    }
  };

  return (
    <>
      {/* WhatsApp FAB */}
      <Tooltip title="Quick WhatsApp Message">
        <IconButton
          color="primary"
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            bgcolor: '#25D366',
            color: 'white',
            '&:hover': {
              bgcolor: '#128C7E',
            },
            width: 56,
            height: 56,
          }}
        >
          <WhatsApp />
        </IconButton>
      </Tooltip>

      {/* Message Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsApp sx={{ color: '#25D366' }} />
          Quick WhatsApp Message
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a template or customize your message. Tap send to open WhatsApp.
          </Typography>

          {/* Message Templates */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            Quick Templates
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {messageTemplates.map((template) => (
              <Chip
                key={template.id}
                label={template.description}
                icon={getCategoryIcon(template.category)}
                color={getCategoryColor(template.category)}
                variant={selectedMessage?.id === template.id ? 'filled' : 'outlined'}
                onClick={() => handleTemplateSelect(template)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>

          {/* Custom Message */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            Message Preview
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Your message will appear here..."
            variant="outlined"
            sx={{ mb: 2 }}
          />

          {/* Current Context */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Context:</strong> {userStatus} • {currentTask} • {currentLocation}
            </Typography>
          </Alert>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<ContentCopy />}
              onClick={handleCopyMessage}
              variant="outlined"
              size="small"
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
            <Button
              startIcon={<Edit />}
              onClick={() => setCustomMessage('')}
              variant="outlined"
              size="small"
            >
              Clear
            </Button>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSendMessage}
            variant="contained"
            startIcon={<WhatsApp />}
            disabled={!customMessage.trim()}
            sx={{
              bgcolor: '#25D366',
              '&:hover': {
                bgcolor: '#128C7E',
              },
            }}
          >
            Send via WhatsApp
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
} 